import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "@/app/store/hooks";
import {
  setChatTurnReaction,
  submitChatTurnFeedbackComment,
} from "@/features/chat/model/chatSlice";
import type { ChatTurnFeedback } from "@/features/chat/model/chatTypes";
import type { Reaction } from "@/shared/lib/feedbackReaction";
import {
  FeedbackThumbs,
  type FeedbackCommentSubmitResult,
  type FeedbackLabels,
} from "@/shared/ui/feedback/FeedbackThumbs";

type ChatTurnFeedbackButtonsProps = {
  turnId: string;
  feedback?: ChatTurnFeedback | null;
};

/** Thin wrapper: supplies the chat Redux thunks and i18n strings to the shared,
 * feature-agnostic thumbs + comment popover control. Mirrors NuggetFeedbackButtons. */
export function ChatTurnFeedbackButtons({ turnId, feedback }: ChatTurnFeedbackButtonsProps) {
  const { t } = useTranslation("chat");
  const dispatch = useAppDispatch();

  const labels = useMemo<FeedbackLabels>(
    () => ({
      thumbUpAriaLabel: t("response.feedback.thumbUpAriaLabel"),
      thumbDownAriaLabel: t("response.feedback.thumbDownAriaLabel"),
      thumbUpTooltip: t("response.feedback.thumbUpTooltip"),
      thumbDownTooltip: t("response.feedback.thumbDownTooltip"),
      commentHeadingUp: t("response.feedback.commentHeadingUp"),
      commentHeadingDown: t("response.feedback.commentHeadingDown"),
      commentSubheading: t("response.feedback.commentSubheading"),
      commentPlaceholderUp: t("response.feedback.commentPlaceholderUp"),
      commentPlaceholderDown: t("response.feedback.commentPlaceholderDown"),
      commentAriaLabel: t("response.feedback.commentAriaLabel"),
      commentError: t("response.feedback.commentError"),
      skip: t("response.feedback.skip"),
      submit: t("response.feedback.submit"),
    }),
    [t],
  );

  const handleReact = useCallback(
    (reaction: Reaction) =>
      dispatch(
        setChatTurnReaction({
          turnId,
          reaction,
          previousFeedback: feedback ?? null,
        }),
      ),
    [dispatch, feedback, turnId],
  );

  const handleSubmitComment = useCallback(
    async (comment: string): Promise<FeedbackCommentSubmitResult> => {
      const result = await dispatch(submitChatTurnFeedbackComment({ turnId, comment }));
      if (submitChatTurnFeedbackComment.rejected.match(result)) {
        return { ok: false, message: result.payload ?? t("response.feedback.commentError") };
      }
      return { ok: true };
    },
    [dispatch, turnId, t],
  );

  return (
    <FeedbackThumbs
      feedback={feedback}
      labels={labels}
      onReact={handleReact}
      onSubmitComment={handleSubmitComment}
    />
  );
}
